import React, { useEffect, useState } from 'react';
import { Button, Table, notification } from 'antd';
import type { TableColumnsType } from 'antd';
import { TableRowSelection } from 'antd/es/table/interface';
import { RoleItem } from 'pages/Role';
import { API_URL } from 'env';
import { UserIcon } from '@phosphor-icons/react';

interface DataType {
    key: string;
    id: string;
    userName: string;
    phoneNumber: string;
    email: string;
}

const columns: TableColumnsType<DataType> = [
    {
        title: 'Name',
        dataIndex: 'userName',
        render: (text: string, record) => (
            <div className="flex items-center gap-2">
                <div className="w-7 h-7 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center shrink-0">
                    <UserIcon size={14} className="text-blue-500" />
                </div>
                <div>
                    <p className="text-sm font-medium m-0">{text}</p>
                    <p className="text-xs text-text-muted m-0">#{record.id}</p>
                </div>
            </div>
        ),
    },
    {
        title: 'Email',
        dataIndex: 'email',
        render: (text: string) => <span className="text-sm text-text-secondary">{text}</span>,
    },
    {
        title: 'Phone',
        dataIndex: 'phoneNumber',
        render: (text: string) => <span className="text-sm text-text-secondary">{text || '—'}</span>,
    },
];

const AddUsersToRoleInterface = ({ role, onClose }: { role: RoleItem; onClose: () => void }) => {
    const [assignUsers, setAssignUsers] = useState<DataType[]>([]);
    const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
    const [defaultSelectedUser, setDefaultSelectedUser] = useState<React.Key[]>([]);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [api, contextHolder] = notification.useNotification();

    const rowSelection: TableRowSelection<DataType> = {
        selectedRowKeys,
        onChange: keys => setSelectedRowKeys(keys),
    };

    async function onAddUserToRole(id: number) {
        await fetch(`${API_URL}api/v1/be/role/assign-user`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${localStorage.getItem('token')}` },
            body: JSON.stringify({ role_id: role.id, user_id: id }),
        });
    }

    async function onRemoveRoleFromUser(id: number) {
        await fetch(`${API_URL}api/v1/be/role/remove-user`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${localStorage.getItem('token')}` },
            body: JSON.stringify({ role_id: role.id, user_id: id }),
        });
    }

    async function onSave() {
        const usersToAdd = selectedRowKeys.filter(key => !defaultSelectedUser.includes(key));
        const usersToRemove = defaultSelectedUser.filter(key => !selectedRowKeys.includes(key));
        setSaving(true);
        try {
            await Promise.all([
                ...usersToAdd.map(userId => onAddUserToRole(Number(userId))),
                ...usersToRemove.map(userId => onRemoveRoleFromUser(Number(userId))),
            ]);
            api.success({ message: 'Members updated successfully', placement: 'topRight' });
            setDefaultSelectedUser(selectedRowKeys);
            onClose();
        } catch (error) {
            api.error({ message: 'Failed to update members', placement: 'topRight' });
        } finally {
            setSaving(false);
        }
    }

    async function fetchData() {
        setLoading(true);
        try {
            const [usersRes, roleRes] = await Promise.all([
                fetch(`${API_URL}api/v1/be/users`, {
                    method: 'GET',
                    headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${localStorage.getItem('token')}` }
                }),
                fetch(`${API_URL}api/v1/be/role/${role.id}`, {
                    method: 'GET',
                    headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${localStorage.getItem('token')}` }
                }),
            ]);
            const usersData = await usersRes.json();
            const roleData = await roleRes.json();

            const users: DataType[] = usersData.data.items.map((item: any) => ({
                key: item.id,
                id: item.id,
                userName: item.fullname,
                phoneNumber: item.phone_number || '',
                email: item.email,
            }));
            setAssignUsers(users);

            const assigned: React.Key[] = roleData.data.users.map((item: any) => item.id);
            setSelectedRowKeys(assigned);
            setDefaultSelectedUser(assigned);
        } catch (error) {
            console.error('Error fetching data:', error);
        } finally {
            setLoading(false);
        }
    }

    useEffect(() => {
        fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const addedCount = selectedRowKeys.filter(key => !defaultSelectedUser.includes(key)).length;
    const removedCount = defaultSelectedUser.filter(key => !selectedRowKeys.includes(key)).length;

    return (
        <>
            {contextHolder}
            <div className="flex flex-col gap-4">
                <div className="flex items-center justify-between">
                    <p className="text-sm text-text-secondary m-0">
                        {selectedRowKeys.length} member{selectedRowKeys.length !== 1 ? 's' : ''} selected
                        {(addedCount > 0 || removedCount > 0) && (
                            <span className="ml-2 text-xs text-text-muted">
                                ({addedCount > 0 && `+${addedCount}`}{addedCount > 0 && removedCount > 0 && ', '}{removedCount > 0 && `-${removedCount}`} pending)
                            </span>
                        )}
                    </p>
                </div>

                <Table
                    rowSelection={rowSelection}
                    columns={columns}
                    dataSource={assignUsers}
                    loading={loading}
                    size="small"
                    scroll={{ x: 'max-content' }}
                    pagination={{ pageSize: 8, showSizeChanger: false, showTotal: total => `${total} users` }}
                    locale={{ emptyText: <div className="py-8 text-center text-text-muted text-sm">No users found</div> }}
                />

                <div className="flex justify-end gap-2 pt-2 border-t border-border">
                    <Button onClick={onClose}>Cancel</Button>
                    <Button type="primary" onClick={onSave} loading={saving}>
                        Save Members
                    </Button>
                </div>
            </div>
        </>
    );
};

export default AddUsersToRoleInterface;
